import type { Building } from "building-types/building/buildingTypes";
import type { OpeningTrim, OpeningTrimOpeningType } from "building-types/casing/openingTrimTypes";
import type { OpeningSide, OpeningSideContext } from "building-types/openings/openingTypes";
import type { WallOpening } from "building-types/openings/wallOpeningTypes";
import { worldInchesNew } from "common/utils/nominalNumbers";
import type { Plane } from "geom/planeTypes";
import { polyLine2Length } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { TrimKeyMeasure } from "../trimTypes";
import { TrimKeyMeasureCondition } from "../trimTypes";

export function getOpeningTrims(building: Building): TrimKeyMeasure[] {
  const measures: TrimKeyMeasure[] = [];

  Object.values(building.doorUnits).reduce((acc, door) => {
    if (door.doorPlacement.opening) {
      acc.push(...openingTrim("DOOR", door.doorPlacement.opening));
    }
    return acc;
  }, measures);
  Object.values(building.windowUnits).reduce((acc, window) => {
    if (window.windowPlacement.opening) {
      acc.push(...openingTrim("WINDOW", window.windowPlacement.opening));
    }
    return acc;
  }, measures);

  return measures;
}

export interface OpeningTrimRail extends TrimKeyMeasure {
  readonly plane: Plane;
  readonly rails: PolyLine2[];
  readonly roomId: string | undefined;
  readonly type: OpeningTrimOpeningType;
  readonly openingId: string | undefined;
  profile?: string;
}

export function getOpeningRails(
  building: Pick<Building, "doorUnits" | "windowUnits">,
  type?: OpeningTrimOpeningType,
  condition?: TrimKeyMeasureCondition,
): OpeningTrimRail[] {
  const measures: OpeningTrimRail[] = [];

  if (!type || type === "DOOR") {
    Object.values(building.doorUnits).reduce((acc, door) => {
      if (door.doorPlacement.opening) {
        const details = openingTrimRail("DOOR", door.doorPlacement.opening);
        details.forEach((rail) => {
          acc.push({
            ...rail,
            handles: door.docEntityHandle !== undefined ? [door.docEntityHandle] : [],
          });
        });
      }
      return acc;
    }, measures);
  }
  if (!type || type === "WINDOW") {
    Object.values(building.windowUnits).reduce((acc, window) => {
      if (window.windowPlacement.opening) {
        const details = openingTrimRail("WINDOW", window.windowPlacement.opening);
        details.forEach((rail) => {
          acc.push({
            ...rail,
            handles: window.docEntityHandle !== undefined ? [window.docEntityHandle] : [],
          });
        });
      }
      return acc;
    }, measures);
  }

  if (condition) {
    return measures.filter((measure) => measure.condition === condition);
  }

  return measures;
}

function openingTrimRail(type: OpeningTrimOpeningType, opening: WallOpening): OpeningTrimRail[] {
  const { back, backPlane, backRoom, front, frontPlane, frontRoom } = opening;
  const result: OpeningTrimRail[] = [];
  back.trim.forEach((trim) => {
    const { rail, elevationProfile } = trim;
    if (rail) {
      result.push({
        ...trimKeyMeasure(type, back.context, trim),
        plane: back.context.openingPlane ?? backPlane,
        rails: elevationProfile ?? [rail],
        roomId: backRoom?.id,
        openingId: opening.handle?.id,
        type,
      });
    }
  });
  front.trim.forEach((trim) => {
    const { rail, elevationProfile } = trim;
    if (rail) {
      result.push({
        ...trimKeyMeasure(type, front.context, trim),
        plane: front.context.openingPlane ?? frontPlane,
        rails: elevationProfile ?? [rail],
        roomId: frontRoom?.id,
        openingId: opening.handle?.id,
        type,
      });
    }
  });
  return result;
}

function openingTrim(type: OpeningTrimOpeningType, opening: WallOpening): TrimKeyMeasure[] {
  const { back, front } = opening;

  return [...sideTrim(type, back), ...sideTrim(type, front)];
}

function sideTrim(type: OpeningTrimOpeningType, side: OpeningSide): TrimKeyMeasure[] {
  return side.trim.map((trim) => trimKeyMeasure(type, side.context, trim));
}

export function trimKeyMeasure(
  type: OpeningTrimOpeningType,
  context: OpeningSideContext,
  trim: OpeningTrim,
): TrimKeyMeasure {
  return {
    condition: context.condition ? TrimKeyMeasureCondition[context.condition] : undefined,
    category: `${type}_${trim.type}`,
    name: `${trim.name}${trim.width ?? ""}${trim.depth && trim.width ? `x${trim.depth}"` : ""}`,
    length: trim.rail ? worldInchesNew(polyLine2Length(trim.rail)) : worldInchesNew(0),
    rail: trim.rail,
    height: trim.width ? worldInchesNew(trim.width) : undefined,
    width: trim.depth ? worldInchesNew(trim.depth) : undefined,
    profileId: trim.profileId,
  };
}
